import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useUpdateCategoryStatus } from '@shared/api/hooks/ccf/reports';
import { CompanyReportCategoryStatusStatusEnum } from '@shared/api/types';
import Alert from '@shared/components/alert';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Button, ButtonProps } from '@shared/components/ui/button';
import { useToast } from '@shared/components/ui/use-toast';
import { ReactNode } from 'react';
import { useCCF } from '../ccf-context';
import ExcludeCategoryDialog from '../components/exclude-category-dialog';
import { ScopeCategorySlug } from '../slugs';
import { useCategory } from '../utils';
import AddDataMenu from '@app/components/imports/scope-1-2-import/components/add-data-menu';
import { isScope1And2Category } from '@app/components/imports/scope-1-2-import/config';

interface CCFCategoryIntroPageProps {
  categorySlug: ScopeCategorySlug;
  description: string | ReactNode;
  primaryActionProps?: ButtonProps;
  primaryActionText?: string;
}

const reasonLabels: Record<string, string> = {
  [CompanyReportCategoryStatusStatusEnum.not_required]: 'Not required',
  [CompanyReportCategoryStatusStatusEnum.no_emissions]:
    'No emissions to report',
  [CompanyReportCategoryStatusStatusEnum.data_unavailable]: 'Data unavailable',
};

export default function CCFCategoryIntroPage({
  categorySlug,
  description,
  primaryActionProps,
  primaryActionText = 'Add data',
}: CCFCategoryIntroPageProps) {
  const { uuid: reportUuid } = useCCF();
  const { toast } = useToast();
  const category = useCategory(categorySlug);
  const { mutateAsync: updateCategoryStatus, isLoading: isUpdating } =
    useUpdateCategoryStatus();

  const {
    showSimpleDialog: showExcludeDialog,
    SimpleDialogComponent: ExcludeDialog,
    closeSimpleDialog: closeExcludeDialog,
  } = useSimpleDialog();

  if (!category) {
    return null;
  }

  const isExcluded =
    category.status === CompanyReportCategoryStatusStatusEnum.not_required ||
    category.status === CompanyReportCategoryStatusStatusEnum.no_emissions ||
    category.status === CompanyReportCategoryStatusStatusEnum.data_unavailable;

  const handleIncludeCategory = async () => {
    try {
      await updateCategoryStatus({
        companyReportUuid: reportUuid!,
        data: {
          activityType: category.slug,
          status: CompanyReportCategoryStatusStatusEnum.included,
        },
      });
      toast({
        title: 'Category included',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Failed to include category',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="max-w-content-center space-y-6 pt-4">
      {typeof description === 'string' ? (
        <Text>{description}</Text>
      ) : (
        <div>{description}</div>
      )}
      {isExcluded ? (
        <Alert
          variant="info"
          title="Category excluded from report"
          icon={<Icons.x className="size-4" />}
        >
          <div className="space-y-4">
            <p>
              This category has been excluded from your report with the
              following reason:{' '}
              <span className="italic">{reasonLabels[category.status]}</span>
            </p>
            <div className="flex justify-between">
              <Button variant="outline" onClick={showExcludeDialog}>
                Update reason
              </Button>
              <Button onClick={handleIncludeCategory} disabled={isUpdating}>
                Include category
              </Button>
            </div>
          </div>
        </Alert>
      ) : (
        <div className="mx-auto flex w-full justify-between">
          <Button variant="secondary" onClick={showExcludeDialog}>
            Exclude from report
          </Button>
          {isScope1And2Category(categorySlug) ? (
            <AddDataMenu
              categorySlug={categorySlug}
              onAddManually={() =>
                primaryActionProps?.onClick?.(undefined as never)
              }
              size="default"
              className="bg-primary-700"
            />
          ) : (
            <Button className="bg-primary-700" {...primaryActionProps}>
              {primaryActionText}
            </Button>
          )}
        </div>
      )}
      {ExcludeDialog({
        header: 'Exclude category from your report',
        contentClassName: 'max-w-lg',
        content: (
          <ExcludeCategoryDialog
            category={category}
            closeDialog={closeExcludeDialog}
          />
        ),
      })}
    </div>
  );
}
